import React, { ChangeEvent, useEffect, useState } from 'react';
import { Typography, Stack, Divider, Autocomplete, TextField, Button, Box, useMediaQuery } from '@mui/material';
import { DeleteOutlined, RefreshOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { CreativeType, CreativeFile } from 'types';
import {
  creativeAcceptMediaTypes,
  getDimensions,
  getMatchingCreativeTypes,
  isNoPreviewByOriginalFileName,
} from 'utils';
import { Card } from '../Card';
import { useCampaign } from 'hooks';

type UploadedCreativePreviewProps = {
  id: string;
  isEdit: boolean;
  isValid?: boolean;
  onDelete: VoidFunction;
  creative: CreativeFile;
  onOpenGallery?: VoidFunction;
  onReplace?: (files: File[]) => void;
  setCreative?: (creative: CreativeFile) => void;
};

const previewSize = { width: '100%', height: 80 };

export const UploadedCreativePreview = ({
  id,
  isEdit,
  isValid,
  onDelete,
  creative,
  onReplace,
  setCreative,
  onOpenGallery,
}: UploadedCreativePreviewProps) => {
  const [fileSizeError, setFileSizeError] = useState<{ isError: boolean; maxSize: number | null }>({
    isError: false,
    maxSize: null,
  });
  const { file, url, originalFileName, creativeType } = creative;
  const { getCreativeTypesQuery } = useCampaign();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [creativeTypeOptions, setCreativeTypeOptions] = useState<CreativeType[]>([]);

  const { creativeTypes = [] } = getCreativeTypesQuery?.data ?? {};

  useEffect(() => {
    const filterOptions = async () => {
      const matchingCreativeTypes = await getMatchingCreativeTypes(file, creativeTypes);
      setCreativeTypeOptions(matchingCreativeTypes);
    };
    filterOptions();
  }, [creativeTypes, file]);

  const formatDimensions = creativeType ? getDimensions(creativeType.width, creativeType.height) : null;

  const onCreativeReplace = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files?.length) {
      return;
    }

    onReplace?.(Array.from(files));

    // this will ensure that the file input is cleared after the file is uploaded
    e.currentTarget.value = '';
  };

  const setCreativeFormat = async (selectedCreativeType: CreativeType | null) => {
    const creativeSize = file.size;
    const selectedCreativeMaxSize = selectedCreativeType?.maxFileSizeBytes ?? 0;
    setCreative?.({ ...creative, creativeType: selectedCreativeType });
    setFileSizeError({ isError: creativeSize > selectedCreativeMaxSize, maxSize: selectedCreativeMaxSize });
  };

  const isEmptyField = !isValid && !creativeType;
  const isError = isEmptyField || fileSizeError.isError;

  const getErrorMessage = () => {
    if (isEmptyField) {
      return 'Wähle das entsprechende Format aus';
    }

    if (fileSizeError.isError) {
      return `Hochgeladene Datei zu gross. Max. ${fileSizeError.maxSize! / 1000} KB für ${creative?.creativeType?.displayName}.`;
    }

    return '';
  };

  const getEditContent = (isEdit: boolean) => ({
    editAction: isEdit ? (
      <Button component="label" variant="text" size="small" startIcon={<RefreshOutlined />}>
        Ersetzen
        <input
          hidden
          multiple
          type="file"
          accept={creativeAcceptMediaTypes}
          data-testid="fileReplace"
          onChange={onCreativeReplace}
        />
      </Button>
    ) : null,
    formatSelection: isEdit ? (
      <Stack>
        {creativeType?.displayName && formatDimensions ? (
          <>
            <Typography variant="h4">{creativeType?.displayName}</Typography>
            <Typography variant="h4">{formatDimensions}</Typography>
          </>
        ) : (
          <>
            <Typography variant="h4">{creative.creativeType?.displayName}</Typography>
            <Typography variant="h4">
              {creative.creativeType?.width}x{creative.creativeType?.height}
            </Typography>
          </>
        )}
      </Stack>
    ) : (
      <Autocomplete
        fullWidth
        size="small"
        value={creativeType}
        disabled={creativeTypeOptions.length === 1}
        options={creativeTypeOptions}
        onChange={(_, value) => setCreativeFormat(value)}
        getOptionLabel={(option) => `${option.displayName} (${getDimensions(option.width, option.height)})`}
        renderInput={(params) => (
          <TextField
            {...params}
            error={isError}
            placeholder="Bitte wählen...."
            label="Creative Format wählen"
            helperText={getErrorMessage()}
          />
        )}
        isOptionEqualToValue={(option, value) => option.displayName === value.displayName}
      />
    ),
    contentAlignment: isEdit ? 'space-between' : '',
    buttonAlignment: isEdit ? 'space-between' : 'end',
    onGalleryOpenAction: isEdit ? onOpenGallery : undefined,
    cursor: isEdit ? 'pointer' : 'unset',
  });

  const { editAction, contentAlignment, formatSelection, buttonAlignment, onGalleryOpenAction, cursor } =
    getEditContent(isEdit);

  const getThumbnail = () => {
    const isNoPeview =
      (originalFileName && isNoPreviewByOriginalFileName(originalFileName)) ||
      (file?.name && isNoPreviewByOriginalFileName(file?.name));

    if (isNoPeview) {
      return (
        <Box height={80} width={142} alignItems="center" justifyContent="center" display="flex" flexDirection="column">
          <VisibilityOffOutlined fontSize="large" />
          <Typography textAlign="center">Keine Vorschau verfügbar</Typography>
        </Box>
      );
    }

    const fileUrl = url || URL.createObjectURL(file);

    if (creativeType?.id?.toLowerCase().includes('video')) {
      return <video src={fileUrl} style={previewSize} />;
    }

    return (
      <Box height={80} width={142} alignItems="center" justifyContent="center" display="flex" id={id}>
        <Box
          component="img"
          sx={{
            maxHeight: 80,
            maxWidth: 142,
          }}
          alt="Thumbnail image"
          src={fileUrl}
        />
      </Box>
    );
  };

  return (
    <Card>
      <Stack
        gap={{ xs: 4, sm: 6 }}
        direction={{ xs: 'column', sm: 'row' }}
        textAlign={{ xs: 'center', sm: 'unset' }}
        alignItems={{ xs: 'center', sm: 'unset' }}
        justifyContent={{ xs: 'center', sm: 'unset' }}
      >
        <Stack gap={2} width={142} sx={{ cursor }} onClick={onGalleryOpenAction}>
          {getThumbnail()}
          <Stack alignItems="center" textAlign="center" sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" color="grey.600" noWrap={isEdit} width={142}>
              {originalFileName || file.name}
            </Typography>
            <Typography variant="body2" color="grey.600">
              {formatDimensions}
            </Typography>
          </Stack>
        </Stack>
        <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />
        <Stack
          flexGrow={1}
          gap={{ xs: 2, sm: 4 }}
          justifyContent={contentAlignment}
          width={1}
          alignItems={isMobile ? 'center' : 'unset'}
        >
          {formatSelection}
          <Stack direction="row" flexWrap="wrap" justifyContent={buttonAlignment}>
            {editAction}
            <Button variant="text" size="small" startIcon={<DeleteOutlined />} onClick={onDelete}>
              Löschen
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
